/**
 * @file Button.tsx
 * @path packages/ui/src/button.component.tsx
 * @description Button component utilizing the neuro-bezel design system.
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@idk/utils";

const buttonVariants = cva(
  // Base classes
  "overflow-hidden inline-flex items-center justify-center whitespace-nowrap rounded text-xs font-bold transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border gap-2 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 disabled:pointer-events-none disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:bg-muted disabled:text-muted-foreground disabled:border-muted hover:disabled:bg-muted",
  {
    variants: {
      variant: {
        // 1. Primary
        default:
          "bg-primary text-primary-foreground border-primary neuro-bezel",

        // 2. Secondary
        secondary:
          "bg-secondary text-secondary-foreground border-input neuro-bezel",

        // 3. Accent
        accent: "bg-accent text-accent-foreground border-accent neuro-bezel",

        // 4. Ghost
        ghost: "border-transparent hover:bg-secondary/50 hover:text-foreground",

        // 5. Link
        link: "text-primary underline-offset-4 hover:text-accent hover:underline border-transparent",

        // 6. Destructive
        destructive:
          "bg-destructive text-destructive-foreground border-destructive neuro-bezel",

        // 7. Outline
        outline:
          "bg-gradient-to-br from-foreground/30 to-background/15 text-foreground border hover:border-primary/50 hover:text-primary neuro-bezel",

        // 8. Glass (UPDATED)
        // - Deeper background (black/40) combined with backdrop-blur
        // - Stark outer border (black/80) to frame the light
        // - Overrides the base text-xs and rounded to match the image
        glass:
          "bg-background/40 backdrop-blur-md border-background/80 text-foreground hover:bg-background/50 active:scale-95 neuro-bezel rounded text-sm",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded px-3",
        lg: "h-12 rounded px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
